import React, { useEffect, useState, useRef } from 'react';

function _arrayLikeToArray(r, a) {
  (null == a || a > r.length) && (a = r.length);
  for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e];
  return n;
}
function _arrayWithHoles(r) {
  if (Array.isArray(r)) return r;
}
function _iterableToArrayLimit(r, l) {
  var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"];
  if (null != t) {
    var e,
      n,
      i,
      u,
      a = [],
      f = !0,
      o = !1;
    try {
      if (i = (t = t.call(r)).next, 0 === l) {
        if (Object(t) !== t) return;
        f = !1;
      } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0);
    } catch (r) {
      o = !0, n = r;
    } finally {
      try {
        if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return;
      } finally {
        if (o) throw n;
      }
    }
    return a;
  }
}
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _slicedToArray(r, e) {
  return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest();
}
function _unsupportedIterableToArray(r, a) {
  if (r) {
    if ("string" == typeof r) return _arrayLikeToArray(r, a);
    var t = {}.toString.call(r).slice(8, -1);
    return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0;
  }
}

/* eslint-disable react-hooks/exhaustive-deps */
var useMountEffect = function useMountEffect(callback) {
  return useEffect(callback, []);
};

var useIsMounted = function useIsMounted() {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isMounted = _useState2[0],
    setIsMounted = _useState2[1];
  useMountEffect(function () {
    setIsMounted(true);
  });
  return isMounted;
};

/* istanbul ignore file */

// This is useful when you want to fire an effect only after you've mounted.
// Useful for example if you have memoized objects you want to export from
// custom hooks but only when they change after the initial construction
var useUpdateEffect = function useUpdateEffect(effect, deps) {
  var mounted = useRef(false);
  useEffect(function () {
    if (mounted.current) {
      return effect();
    }
    mounted.current = true;
  }, deps);
};

// https://www.joshwcomeau.com/snippets/react-hooks/use-timeout/
function useTimeout(callback, delay) {
  var timeoutRef = useRef(null);
  var savedCallback = useRef(callback);
  useEffect(function () {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(function () {
    var tick = function tick() {
      return savedCallback.current();
    };
    if (typeof delay === 'number') {
      timeoutRef.current = window.setTimeout(tick, delay);
      return function () {
        return window.clearTimeout(timeoutRef.current);
      };
    }
  }, [delay]);
  return timeoutRef;
}

var usePrevious = function usePrevious(value) {
  var defaultValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var ref = useRef(defaultValue);
  // Store current value in ref
  useEffect(function () {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
};

function useInterval(callback, interval) {
  var intervalRef = useRef(null);
  var savedCallback = useRef();
  useEffect(function () {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(function () {
    var handler = function handler() {
      return savedCallback.current.apply(savedCallback, arguments);
    };
    if (interval) {
      intervalRef.current = setInterval(handler, interval);
      return function () {
        return clearInterval(intervalRef.current);
      };
    }
  }, [interval]);
  return intervalRef;
}

function useOnce(initializer) {
  var value = useState(initializer)[0];
  return value;
}

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return _assign.apply(this, arguments);
};
function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}

function useFlow(_a) {
  var _b;
  var definition = _a.definition,
    context = _a.context,
    callbacks = _a.callbacks;
  var PROGRESS_INCREMENT_FACTOR = 0.2;
  var firstStepId = definition[0].id;
  var _c = useState(function () {
      var _a;
      return _a = {}, _a[firstStepId] = false, _a;
    }),
    touchedMap = _c[0],
    setTouchedMap = _c[1];
  var _d = useState([firstStepId]),
    stepsVisited = _d[0],
    setStepsVisited = _d[1];
  var _e = useState([]),
    progressIncrements = _e[0],
    setProgressIncrements = _e[1];
  var progress = progressIncrements.reduce(function (sum, a) {
    return sum + a;
  }, 0);
  var remainingProgress = 100 - progress;
  var stepMap = React.useMemo(function () {
    return definition.reduce(function (acc, step) {
      acc[step.id] = step;
      return acc;
    }, {});
  }, [definition]);
  var _f = React.useState(firstStepId),
    currentStep = _f[0],
    setCurrentStep = _f[1];
  var touched = (_b = touchedMap[currentStep]) !== null && _b !== void 0 ? _b : false;
  var setTouched = function setTouched(flag) {
    setTouchedMap(function (prev) {
      var _a;
      return _assign(_assign({}, prev), (_a = {}, _a[currentStep] = flag, _a));
    });
  };
  var actions = React.useMemo(function () {
    return {
      goToNextStep: function goToNextStep(props) {
        var _a, _b;
        var propsAndContext = _assign(_assign({}, props), {
          context: context
        });
        var stepDef = stepMap[currentStep];
        var isLast = stepDef.isLastStep(propsAndContext);
        if (isLast) {
          (_a = callbacks === null || callbacks === void 0 ? void 0 : callbacks.onComplete) === null || _a === void 0 ? void 0 : _a.call(callbacks, currentStep);
          return;
        }
        var nextStepId = stepDef.next(propsAndContext);
        if (!nextStepId) return;
        setCurrentStep(nextStepId);
        setTouchedMap(function (prev) {
          var _a;
          var _b;
          return _assign(_assign({}, prev), (_a = {}, _a[nextStepId] = (_b = prev[nextStepId]) !== null && _b !== void 0 ? _b : false, _a));
        });
        setStepsVisited(function (v) {
          return __spreadArray(__spreadArray([], v, true), [nextStepId], false);
        });
        var nextIncrement = stepDef.isLastStep(propsAndContext) ? remainingProgress : remainingProgress * PROGRESS_INCREMENT_FACTOR;
        setProgressIncrements(function (i) {
          return __spreadArray(__spreadArray([], i, true), [nextIncrement], false);
        });
        (_b = callbacks === null || callbacks === void 0 ? void 0 : callbacks.onAdvance) === null || _b === void 0 ? void 0 : _b.call(callbacks, currentStep, nextStepId);
      },
      goToPreviousStep: function goToPreviousStep() {
        if (stepsVisited.length <= 1) return;
        setProgressIncrements(function (i) {
          return i.slice(0, -1);
        });
        setStepsVisited(function (prev) {
          var _a;
          var newHistory = prev.slice(0, -1);
          var previousStepId = newHistory[newHistory.length - 1];
          (_a = callbacks === null || callbacks === void 0 ? void 0 : callbacks.onRetreat) === null || _a === void 0 ? void 0 : _a.call(callbacks, currentStep, previousStepId);
          setCurrentStep(previousStepId);
          return newHistory;
        });
      },
      getCurrentProgress: function getCurrentProgress() {
        return progress;
      },
      resetFlow: function resetFlow() {
        var _a;
        setCurrentStep(firstStepId);
        setStepsVisited([firstStepId]);
        setProgressIncrements([]);
        setTouchedMap((_a = {}, _a[firstStepId] = false, _a));
      }
    };
  }, [context, stepMap, currentStep, remainingProgress, callbacks, stepsVisited.length, progress, firstStepId]);
  var CurrentStep = stepMap[currentStep];
  return {
    actions: actions,
    CurrentStep: CurrentStep,
    touched: touched,
    setTouched: setTouched
  };
}

export { useFlow, useInterval, useIsMounted, useMountEffect, useOnce, usePrevious, useTimeout, useUpdateEffect };
//# sourceMappingURL=hooks.js.map
